import type { CampaignAdProduct } from '@/types/spCampaigns';
import type {
  HourlyBudgetCampaignIdentity,
  HourlyBudgetListItem,
  HourlyBudgetListParams,
} from '@/types/hourlyBudget';
import { formatWeekdayRange, isAllWeekdays, parseScheduleMode } from '@/utils/hourlyBudgetWeek';

export function campaignHourlyBudgetQueryKey(profileId: string, campaignRef: string) {
  return ['campaign-hourly-budget', profileId, campaignRef] as const;
}

export function campaignHourlyBudgetRunLogsQueryKey(profileId: string, campaignRef: string) {
  return ['campaign-hourly-budget-run-logs', profileId, campaignRef] as const;
}

export function profileHourlyBudgetsQueryKey(
  profileId: string,
  filters?: Pick<HourlyBudgetListParams, 'q' | 'active' | 'product' | 'kind' | 'limit' | 'offset'>
) {
  return ['profile-hourly-budgets', profileId, filters ?? {}] as const;
}

export function normalizeHourlyBudgetProduct(raw: string | null | undefined): CampaignAdProduct {
  const value = (raw ?? 'SP').trim().toUpperCase();
  if (value === 'SB') return 'SB';
  if (value === 'SD') return 'SD';
  return 'SP';
}

export function hourlyBudgetCampaigns(item: HourlyBudgetListItem): HourlyBudgetCampaignIdentity[] {
  if (Array.isArray(item.campaigns) && item.campaigns.length > 0) {
    return item.campaigns;
  }
  return item.campaign ? [item.campaign] : [];
}

export function hourlyBudgetCampaignRef(item: HourlyBudgetListItem): string {
  return hourlyBudgetCampaigns(item)[0]?.campaign_id ?? item.campaign?.campaign_id ?? '';
}

export function hourlyBudgetProduct(item: HourlyBudgetListItem): CampaignAdProduct {
  return normalizeHourlyBudgetProduct(hourlyBudgetCampaigns(item)[0]?.campaign_type ?? item.campaign?.campaign_type);
}

export function hourlyBudgetProductsLabel(item: HourlyBudgetListItem): string {
  const products = [
    ...new Set(hourlyBudgetCampaigns(item).map((campaign) => normalizeHourlyBudgetProduct(campaign.campaign_type))),
  ];
  return products.join(', ') || 'SP';
}

export function hourlyBudgetScopeLabel(item: HourlyBudgetListItem): string {
  const campaigns = hourlyBudgetCampaigns(item);
  if (item.source_type === 'portfolio') {
    const name = item.portfolio?.name || item.portfolio?.portfolioId || 'Portfolio';
    const count = campaigns.length;
    return `${name} (${count} campaign${count === 1 ? '' : 's'})`;
  }
  if (campaigns.length <= 1) {
    return campaigns[0]?.name || campaigns[0]?.campaign_id || item.id;
  }
  const first = campaigns[0]?.name || campaigns[0]?.campaign_id || 'Campaign';
  return `${first} +${campaigns.length - 1}`;
}

export function hourlyBudgetGroupId(item: HourlyBudgetListItem): string | null {
  return item.group_id ?? null;
}

export type DaypartingRunStatus = {
  label: 'Active' | 'Inactive' | 'Not running';
  detail: string | null;
  tone: 'active' | 'inactive' | 'blocked';
};

function campaignState(state: string | null | undefined): string {
  return (state ?? '').trim().toUpperCase();
}

function isCampaignEnabled(state: string | null | undefined): boolean {
  return campaignState(state) === 'ENABLED';
}

function blockedReason(state: string): 'paused' | 'archived' | 'not enabled' {
  if (state === 'PAUSED') return 'paused';
  if (state === 'ARCHIVED') return 'archived';
  return 'not enabled';
}

function blockedDetail(states: string[]): string {
  const reasons = states.map((state) => blockedReason(state));
  const paused = reasons.filter((reason) => reason === 'paused').length;
  const archived = reasons.filter((reason) => reason === 'archived').length;
  const other = reasons.length - paused - archived;
  const parts: string[] = [];
  if (paused) parts.push(`${paused} paused`);
  if (archived) parts.push(`${archived} archived`);
  if (other) parts.push(`${other} not enabled`);
  return parts.join(', ');
}

/** Schedule switch stays `active`. Campaign state only changes how the row reads. */
export function daypartingRunStatus(item: HourlyBudgetListItem): DaypartingRunStatus {
  if (!item.active) {
    return { label: 'Inactive', detail: null, tone: 'inactive' };
  }
  const campaigns = hourlyBudgetCampaigns(item);
  const blocked = campaigns.filter((campaign) => !isCampaignEnabled(campaign.state));
  if (blocked.length === 0) {
    return { label: 'Active', detail: null, tone: 'active' };
  }
  if (blocked.length === campaigns.length) {
    if (campaigns.length === 1) {
      const reason = blockedReason(campaignState(campaigns[0]?.state));
      const detail = reason === 'not enabled' ? 'Campaign is not enabled' : `Campaign is ${reason}`;
      return { label: 'Not running', detail, tone: 'blocked' };
    }
    return {
      label: 'Not running',
      detail: blockedDetail(blocked.map((campaign) => campaignState(campaign.state))),
      tone: 'blocked',
    };
  }
  return {
    label: 'Active',
    detail: blockedDetail(blocked.map((campaign) => campaignState(campaign.state))),
    tone: 'active',
  };
}

export function campaignDetailsPath(
  profileId: string,
  campaignId: string,
  product: CampaignAdProduct
): string {
  return `/accounts/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}?product=${encodeURIComponent(product)}`;
}

export function formatHourlyBudgetTimeLocal(value: string | null | undefined): string {
  const raw = String(value ?? '').trim();
  const match = /^(\d{1,2}):(\d{2})/.exec(raw);
  if (!match) return raw;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

export function formatOperatingWindows(
  windows:
    | Array<{ start_time_local: string; end_time_local: string; days_of_week?: number[] }>
    | null
    | undefined,
  _scheduleMode?: string
): string {
  if (!windows || windows.length === 0) {
    return '24/7';
  }
  if (windows.length === 1) {
    const only = windows[0];
    const start = formatHourlyBudgetTimeLocal(only.start_time_local);
    const end = formatHourlyBudgetTimeLocal(only.end_time_local);
    if (start === '00:00' && (end === '23:59' || end === '24:00') && isAllWeekdays(only.days_of_week)) {
      return '24/7';
    }
  }
  const daySpecific = windows.some(
    (window) => Array.isArray(window.days_of_week) && !isAllWeekdays(window.days_of_week)
  );
  const formatted = [...windows]
    .sort((a, b) =>
      formatHourlyBudgetTimeLocal(a.start_time_local).localeCompare(
        formatHourlyBudgetTimeLocal(b.start_time_local)
      )
    )
    .map((window) => {
      const range = `${formatHourlyBudgetTimeLocal(window.start_time_local)}–${formatHourlyBudgetTimeLocal(window.end_time_local)}`;
      if (!daySpecific) return range;
      return `${formatWeekdayRange(window.days_of_week ?? [])} ${range}`;
    });
  return formatted.join(daySpecific ? '; ' : ', ');
}

export function formatBudgetPair(item: {
  hourly_budget?: number | null;
  base_budget?: number | null;
  schedule_mode?: string | null;
  day_settings?: Array<{
    day_of_week: number;
    hourly_budget: number | null;
    base_budget: number | null;
  }> | null;
}): { baseLine: string; hourlyLine: string; weekly?: string } {
  if (parseScheduleMode(item.schedule_mode) === 'weekly' && Array.isArray(item.day_settings) && item.day_settings.length > 0) {
    const parts = [...item.day_settings]
      .sort((a, b) => a.day_of_week - b.day_of_week)
      .map((row) => {
        const day = formatWeekdayRange([row.day_of_week]);
        return `${day} ${formatBudgetAmount(row.base_budget)} / ${formatBudgetAmount(row.hourly_budget)}`;
      });
    return {
      baseLine: `Weekly (${parts.length} day${parts.length === 1 ? '' : 's'})`,
      hourlyLine: parts.join('; '),
      weekly: parts.join('; '),
    };
  }
  return {
    baseLine: `Base ${formatBudgetAmount(item.base_budget)}`,
    hourlyLine: `Hourly ${formatBudgetAmount(item.hourly_budget)}`,
  };
}

export function formatBudgetAmount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return String(value);
}

export function formatMultiplierWindowsSummary(item: {
  time_windows?: unknown[] | null;
  current_effective_increment?: number | null;
  outside_operating_window?: boolean | null;
}): string {
  const count = Array.isArray(item.time_windows) ? item.time_windows.length : 0;
  const countLabel = `${count} window${count === 1 ? '' : 's'}`;
  if (item.outside_operating_window === true || item.current_effective_increment == null) {
    return countLabel;
  }
  return `${countLabel} · ${item.current_effective_increment} now`;
}

export function configuredHourlyBudgetCampaignIds(items: HourlyBudgetListItem[]): string[] {
  const ids: string[] = [];
  for (const item of items) {
    for (const campaign of hourlyBudgetCampaigns(item)) {
      if (campaign.campaign_id) ids.push(campaign.campaign_id);
    }
  }
  return ids;
}

export function excludeHourlyBudgetCampaigns<T extends { campaignId: string }>(
  options: T[],
  configuredCampaignIds: Iterable<string>
): T[] {
  const skip = new Set(configuredCampaignIds);
  return options.filter((option) => !skip.has(option.campaignId));
}
